import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

let echoInstance = null;

function getXsrfToken() {
    const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
}

function getAuthHeaders() {
    const headers = {
        Accept: 'application/json',
    };

    const xsrf = getXsrfToken();
    if (xsrf) {
        headers['X-XSRF-TOKEN'] = xsrf;
    }

    return headers;
}

/**
 * Resolve client WebSocket settings.
 *
 * Two modes:
 * 1) Proxied (default, production): wss on 443 via nginx → Reverb :8081 (/app)
 *    VITE_REVERB_USE_SAME_ORIGIN=true, VITE_REVERB_PORT=443, VITE_REVERB_SCHEME=https
 *
 * 2) Direct (local dev): ws to Reverb port 8081 (page must be http, or Reverb must have TLS)
 *    VITE_REVERB_CONNECT_DIRECT=true, VITE_REVERB_SERVER_PORT=8081, VITE_REVERB_SCHEME=http
 */
function resolveReverbClientConfig() {
    const key = import.meta.env.VITE_REVERB_APP_KEY;

    if (!key) {
        console.error(
            '[Echo] VITE_REVERB_APP_KEY is missing. Rebuild frontend after updating .env (npm run dev / npm run build).'
        );
        return null;
    }

    const connectDirect = import.meta.env.VITE_REVERB_CONNECT_DIRECT === 'true';
    const useSameOrigin = import.meta.env.VITE_REVERB_USE_SAME_ORIGIN === 'true';

    let wsHost;
    let scheme;
    let port;

    if (connectDirect) {
        // Connect straight to the Reverb process (no nginx /app proxy).
        wsHost = import.meta.env.VITE_REVERB_HOST ?? window.location.hostname;
        scheme = import.meta.env.VITE_REVERB_SCHEME ?? 'http';
        port = Number(
            import.meta.env.VITE_REVERB_SERVER_PORT
            ?? import.meta.env.VITE_REVERB_PORT
            ?? 8081
        );
    } else if (useSameOrigin) {
        // Same host as the SPA; nginx must proxy /app → 127.0.0.1:8081
        wsHost = window.location.hostname;
        scheme = window.location.protocol === 'https:' ? 'https' : 'http';
        port = Number(import.meta.env.VITE_REVERB_PORT ?? (scheme === 'https' ? 443 : 80));
    } else {
        wsHost = import.meta.env.VITE_REVERB_HOST ?? window.location.hostname;
        scheme = import.meta.env.VITE_REVERB_SCHEME ?? 'https';
        port = Number(import.meta.env.VITE_REVERB_PORT ?? (scheme === 'https' ? 443 : 80));
    }

    const forceTLS = scheme === 'https';

    return { key, wsHost, port, forceTLS, connectDirect, useSameOrigin };
}

/**
 * Create (or return) the singleton Echo instance for Reverb.
 */
export function getEcho() {
    if (echoInstance) {
        return echoInstance;
    }

    const config = resolveReverbClientConfig();
    if (!config) {
        return null;
    }

    const { key, wsHost, port, forceTLS, connectDirect, useSameOrigin } = config;

    window.Pusher = Pusher;

    echoInstance = new Echo({
        broadcaster: 'reverb',
        key,
        wsHost,
        wsPort: port,
        wssPort: port,
        forceTLS,
        enabledTransports: ['ws', 'wss'],
        disableStats: true,
        // Required for Sanctum cookie SPA auth on /broadcasting/auth
        withCredentials: true,
        authEndpoint: `${window.location.origin}/broadcasting/auth`,
        auth: {
            headers: getAuthHeaders(),
        },
    });

    window.Echo = echoInstance;

    const pusher = echoInstance.connector?.pusher;
    if (pusher?.connection) {
        pusher.connection.bind('state_change', (states) => {
            if (import.meta.env.DEV) {
                console.info('[Echo] connection', states.previous, '→', states.current);
            }
        });
        pusher.connection.bind('error', (err) => {
            console.error('[Echo] connection error', err);
            if (!connectDirect && useSameOrigin && forceTLS) {
                console.error(
                    '[Echo] If state stays "unavailable", ensure nginx proxies /app to Reverb (see docs/reverb-nginx.md) and run: php artisan reverb:start --port=8081'
                );
            }
        });
    }

    if (import.meta.env.DEV) {
        console.info('[Echo] config', { wsHost, port, forceTLS, connectDirect, useSameOrigin });
    }

    return echoInstance;
}

export function disconnectEcho() {
    if (echoInstance) {
        echoInstance.disconnect();
        echoInstance = null;
        window.Echo = undefined;
    }
}

export function getEchoConnectionState() {
    return window.Echo?.connector?.pusher?.connection?.state ?? 'not_initialized';
}
